import type React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { getScheduledTour } from 'app/shared/utils/listingUtils';
import { TOUR_TYPES } from 'app/shared/modules/hdp/InstantTour/constants';
import dateUtils from 'app/shared/utils/dateUtils';
import { Button } from '../../../ui/Button';
import { CalendarIcon } from '../../../ui/icons/CalendarIcon';
import { MessageIcon } from '../../../ui/icons/MessageIcon';
import { TourGroupIcon } from '../../../ui/icons/TourGroupIcon';
import { SelfGuidedTourIcon } from '../../../ui/icons/SelfGuidedTourIcon';
import { LiveVideoTourIcon } from '../../../ui/icons/LiveVideoTourIcon';
import { useContactFormAction } from '../ContactForm/useContactFormAction';
import { useTourAction } from '../TourForm/useTourAction';
import * as styles from './tourConfirmationModule.styles';

const TOUR_TYPE_LABELS: Record<string, string> = {
  [TOUR_TYPES.IN_PERSON]: 'In-person tour',
  [TOUR_TYPES.SELF_GUIDED]: 'Self-guided tour',
  [TOUR_TYPES.LIVE_VIDEO]: 'Live video tour',
};

const TOUR_TYPE_ICONS: Record<string, React.FC> = {
  [TOUR_TYPES.IN_PERSON]: TourGroupIcon,
  [TOUR_TYPES.SELF_GUIDED]: SelfGuidedTourIcon,
  [TOUR_TYPES.LIVE_VIDEO]: LiveVideoTourIcon,
};

const formatTourDateTime = (dateTime: string, zoneId: string, dateOnly: boolean): string => {
  const pattern = dateOnly ? 'EEEE, MMM dd' : "EEEE, MMM dd 'at' h:mm a";
  return dateUtils.parseISODateString(dateTime, zoneId, pattern) as string;
};

export const TourConfirmationBanner: React.FC = () => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const scheduledTours = useSelector((state: RootReduxState) => state.user.scheduledTours);
  const { openContactForm, contactFormOverlay } = useContactFormAction();
  const { openTour, openTourReschedule, tourOverlay } = useTourAction();

  const aliasEncoded = listing?.aliasEncoded;
  const scheduledTour = aliasEncoded ? getScheduledTour(aliasEncoded, scheduledTours) : {};
  const startTime = scheduledTour.startTime ?? '';
  const dateTime = startTime ? dateUtils.appendTimeISOSubstringIfMissing(startTime) : '';

  const [isPast, setIsPast] = useState(() => (dateTime ? dateUtils.isDateInThePast(dateTime) : false));

  useEffect(() => {
    if (!dateTime) return;

    if (dateUtils.isDateInThePast(dateTime)) {
      setIsPast(true);
      return;
    }

    const msUntilPast = new Date(dateTime).getTime() - Date.now();
    if (msUntilPast <= 0) {
      setIsPast(true);
      return;
    }

    const timer = setTimeout(() => setIsPast(true), msUntilPast);
    return () => clearTimeout(timer);
  }, [dateTime]);

  if (!listing || !scheduledTour.tourId) return null;

  const { tourType, zoneId } = scheduledTour;
  const tourLabel = TOUR_TYPE_LABELS[tourType] ?? 'Tour';
  const dateOnly = startTime === dateTime.split('T')[0];
  const formattedDateTime = startTime ? formatTourDateTime(dateTime, zoneId, dateOnly) : '';
  const TourIcon = TOUR_TYPE_ICONS[tourType] ?? TourGroupIcon;

  return (
    <>
      <div className={styles.container} role="status">
        <div className={styles.tourInfoRow}>
          <TourIcon />
          <span className={styles.tourTypeLabel}>Booked {tourLabel.toLowerCase()}</span>
        </div>
        {formattedDateTime && <p className={styles.tourDateTime}>{formattedDateTime}</p>}
        <div className={styles.ctaRow}>
          <Button
            shape="rectangle"
            tone="brand"
            emphasis="outlined"
            size="sm"
            icon={<MessageIcon />}
            onClick={openContactForm}
            aria-label="Contact property"
          >
            Contact
          </Button>
          <Button
            shape="rectangle"
            tone="brand"
            emphasis="outlined"
            size="sm"
            icon={<CalendarIcon />}
            onClick={isPast ? openTour : openTourReschedule}
            aria-label={isPast ? 'Book another tour' : 'Reschedule tour'}
          >
            {isPast ? 'Book another' : 'Reschedule'}
          </Button>
        </div>
      </div>
      {contactFormOverlay}
      {tourOverlay}
    </>
  );
};
